import { getApiBaseUrl } from '../../../utils/apiConfig';

const API_BASE = `${getApiBaseUrl()}/api/cadastros/compatibilidade-equipamentos`;

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function parseJson(res) {
  try {
    return await res.json();
  } catch {
    return { success: false, message: 'Resposta inválida do servidor' };
  }
}

async function request(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) },
  });
  const result = await parseJson(res);
  if (!res.ok || !result.success) {
    throw new Error(result.message || 'Falha na requisição');
  }
  return result.data;
}

export function fetchCompatEquipments() {
  return request('/equipments');
}

export function fetchCompatSubgroupConfig() {
  return request('/subgroup-config');
}

export function saveCompatSubgroupConfig(configType, items) {
  return request('/subgroup-config', {
    method: 'PUT',
    body: JSON.stringify({ configType, items }),
  });
}

export function fetchCompatParts() {
  return request('/parts');
}

export function fetchCompatExternalCompat(equipmentCode) {
  return request(`/equipments/${encodeURIComponent(equipmentCode)}/external-compat`);
}

export function fetchCompatLinks(equipmentCode) {
  return request(`/equipments/${encodeURIComponent(equipmentCode)}/links`);
}

export function fetchCompatEquipmentLinksByProduct(productCode) {
  return request(`/parts/${encodeURIComponent(productCode)}/equipment-links`);
}

export function upsertCompatLink(equipmentCode, body) {
  return request(`/equipments/${encodeURIComponent(equipmentCode)}/links`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function unlinkCompatProduct(equipmentCode, productCode) {
  return request(
    `/equipments/${encodeURIComponent(equipmentCode)}/links/${encodeURIComponent(productCode)}`,
    { method: 'DELETE' }
  );
}

export function fetchCompatPhoto(equipmentCode) {
  return request(`/equipments/${encodeURIComponent(equipmentCode)}/photo`);
}

export function fetchCompatAiSearch(equipmentCode, body) {
  return request(`/equipments/${encodeURIComponent(equipmentCode)}/ai-search`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
}

export function fetchKnowledgeCoverage(items) {
  return request('/knowledge/coverage', {
    method: 'POST',
    body: JSON.stringify({ items: items || [] }),
  });
}

export function fetchKnowledgeLibrary() {
  return request('/knowledge/library');
}

const modeloLabelCache = new Map();

export function suggestKnowledgeModelo({ marca, nome }) {
  const key = `${marca}\u0000${nome}`;
  if (modeloLabelCache.has(key)) return modeloLabelCache.get(key);
  const pending = request('/knowledge/modelo', {
    method: 'POST',
    body: JSON.stringify({ marca, nome }),
  }).catch((err) => {
    modeloLabelCache.delete(key);
    throw err;
  });
  modeloLabelCache.set(key, pending);
  return pending;
}

export function fetchKnowledgeBase({ marca, modelo }) {
  const q = new URLSearchParams({
    marca: marca || '',
    modelo: modelo || '',
  });
  return request(`/knowledge?${q.toString()}`);
}

export async function uploadKnowledgeManual(fields) {
  const token = localStorage.getItem('authToken');
  const body = new FormData();
  body.set('file', fields.file);
  body.set('sectorId', String(fields.sectorId));
  if (fields.marca) body.set('marca', fields.marca);
  if (fields.modelo) body.set('modelo', fields.modelo);
  if (fields.modelosExtra) body.set('modelosExtra', fields.modelosExtra);
  if (fields.nomeDoc) body.set('nomeDoc', fields.nomeDoc);
  if (fields.isUpdate) body.set('isUpdate', 'true');
  if (fields.forceUpload) body.set('forceUpload', 'true');
  const res = await fetch(`${API_BASE}/knowledge/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body,
  });
  const result = await parseJson(res);
  return {
    ok: res.ok && result.success,
    status: res.status,
    message: result.message || '',
    conflict: result.conflict || null,
    data: result.data || null,
  };
}

export function retryKnowledgeJob(jobId, { forceUpload = false } = {}) {
  return request(`/knowledge/jobs/${encodeURIComponent(jobId)}/retry`, {
    method: 'POST',
    body: JSON.stringify({ forceUpload }),
  });
}

export function discardKnowledgeJob(jobId) {
  return request(`/knowledge/jobs/${encodeURIComponent(jobId)}`, { method: 'DELETE' });
}

export function deleteKnowledgeDocument(docId) {
  return request(`/knowledge/documents/${encodeURIComponent(docId)}`, { method: 'DELETE' });
}

export async function uploadCompatPhoto(equipmentCode, file) {
  const code = encodeURIComponent(equipmentCode);
  const presign = await request(`/equipments/${code}/photo/presign`, {
    method: 'POST',
    body: JSON.stringify({
      fileName: file.name,
      mimeType: file.type || 'image/jpeg',
      sizeBytes: file.size,
    }),
  });
  const { uploadUrl, key, mime } = presign;
  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': mime || file.type || 'image/jpeg' },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error('Falha no upload da imagem');
  }
  return request(`/equipments/${code}/photo/complete`, {
    method: 'POST',
    body: JSON.stringify({
      s3Key: key,
      fileName: file.name,
      mimeType: file.type || mime,
      sizeBytes: file.size,
    }),
  });
}
